import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import { RinexPageContent } from './RinexPageContent';
import { SatelliteIcon } from './icons.tsx';

/**
 * RINEX Agent Subdomain Page
 * 
 * Marketing and documentation page for the RINEX Agent.
 * RINEX (Receiver Independent Exchange Format) is the standard format for 
 * raw GNSS (Global Navigation Satellite System) observations.
 * 
 * Processing is handled in the main app with API key authentication.
 */

export function RinexSubdomainPage() {

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-white/[0.08] bg-gray-950/85 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SatelliteIcon className="w-8 h-8 text-lime-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">
                  RINEX <span className="text-lime-400">Agent</span>
                </h1>
                <p className="text-sm text-gray-400">Premium GNSS Data Processing</p>
              </div>
            </div>
            <a
              href="https://landsurv.ai"
              className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-500/20"
            >
              Back to App
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid items-center gap-12 overflow-hidden rounded-2xl border border-lime-400/20 bg-gradient-to-br from-lime-950/35 via-gray-900/80 to-emerald-950/35 p-6 shadow-2xl shadow-lime-950/20 md:grid-cols-2 sm:p-10">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 bg-lime-500/10 border border-lime-500/30 rounded-full">
              <Lock className="w-4 h-4 text-lime-400" />
              <span className="text-sm text-lime-400 font-semibold">Premium Feature</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Professional RINEX File Processing
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Convert, analyze, and process RINEX observation files from any GNSS receiver. 
              Support for multi-constellation data with advanced filtering and quality checks.
            </p>
            <div className="flex gap-4">
              <a
                href="https://landsurv.ai"
                className="px-6 py-3 bg-lime-500 text-gray-900 font-semibold rounded-lg hover:bg-lime-400 transition flex items-center gap-2"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition"
              >
                Learn More
              </a>
            </div>
          </div>
          <div className="relative h-80 bg-gradient-to-br from-lime-500/20 to-green-500/20 rounded-xl border border-lime-500/30 flex items-center justify-center">
            <div className="text-center">
              <SatelliteIcon className="w-32 h-32 text-lime-400/60 mx-auto mb-4 animate-pulse" />
              <p className="text-gray-300 font-semibold">Multi-Constellation GNSS Support</p>
              <p className="text-sm text-gray-400">GPS, GLONASS, Galileo, BeiDou, QZSS, NavIC</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <RinexPageContent />
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-2xl border border-lime-400/20 bg-gradient-to-r from-lime-500/20 to-green-500/20 p-8 text-center">
          <SatelliteIcon className="w-16 h-16 text-lime-400 mx-auto mb-4" />
          <h3 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h3>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Join surveyors and GNSS professionals using LandSurv.ai for advanced data processing.
            Sign up today to access the RINEX Agent with your API key.
          </p>
          <a
            href="https://landsurv.ai"
            className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-gray-900 font-bold rounded-lg hover:bg-lime-400 transition text-lg"
          >
            Get Started with LandSurv.ai
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-700 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-white mb-4">RINEX Agent</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-lime-400 transition">Features</a></li>
                <li><a href="#" className="hover:text-lime-400 transition">Pricing</a></li>
                <li><a href="#" className="hover:text-lime-400 transition">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-lime-400 transition">GNSS Standards</a></li>
                <li><a href="#" className="hover:text-lime-400 transition">API Reference</a></li>
                <li><a href="#" className="hover:text-lime-400 transition">Examples</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-lime-400 transition">Help Center</a></li>
                <li><a href="#" className="hover:text-lime-400 transition">Contact</a></li>
                <li><a href="#" className="hover:text-lime-400 transition">Status</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="https://landsurv.ai" className="hover:text-lime-400 transition">Main App</a></li>
                <li><a href="https://about.landsurv.ai" className="hover:text-lime-400 transition">About</a></li>
                <li><a href="https://release.landsurv.ai" className="hover:text-lime-400 transition">Release Log</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex justify-between items-center">
            <p className="text-sm text-gray-400">© 2025 LandSurv.ai™. All rights reserved.</p>
            <p className="text-sm text-gray-400">RINEX Agent • Premium GNSS Processing</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
